// Shared record/list pieces (other Business UI handoff). Lifted from the
// presentations list/editor (W10) so Clients, Projects and the estimate read
// the same way: a metric strip, underline filter tabs, a facts row under a
// record title, and the design table classes.
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

// ─── Design table ────────────────────────────────────────────────────────
export const TABLE_WRAP = "overflow-x-auto rounded-md border border-rule bg-white";
export const TABLE_HEAD = "border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[11px] font-medium text-muted";
export const TH = "px-[18px] py-[15px] font-medium";
export const TD = "px-[18px] py-[15px]";
export const TROW = "border-b border-[#e7e7e4] last:border-0 hover:bg-[#fafaf8]";

// ─── Metric strip ────────────────────────────────────────────────────────
export interface MetricCell {
  label: string;
  value: number | string | null;
  note?: string;
  onClick?: () => void;
  /** Warn tone for a number that needs attention (still carries a label). */
  attention?: boolean;
}

export function MetricStrip({ cells, ariaLabel }: { cells: MetricCell[]; ariaLabel: string }) {
  return (
    <section aria-label={ariaLabel}>
      <div
        className={cn(
          "grid grid-cols-2 gap-px overflow-hidden rounded-md border border-rule bg-rule",
          cells.length >= 4 ? "lg:grid-cols-4" : "lg:grid-cols-3",
        )}
      >
        {cells.map((c) => {
          const body = (
            <>
              <span className="text-xs text-muted">{c.label}</span>
              {c.value === null ? (
                <span className="mt-2 h-7 w-12 animate-pulse rounded bg-canvas" aria-hidden />
              ) : (
                <span
                  className={cn(
                    "mt-1.5 text-[29px] font-medium leading-tight tracking-[-0.8px] tabular-nums",
                    c.attention ? "text-warn" : "text-ink",
                  )}
                >
                  {typeof c.value === "number" ? c.value.toLocaleString() : c.value}
                </span>
              )}
              {c.note ? <span className="mt-1.5 text-xs text-muted">{c.note}</span> : null}
            </>
          );
          return c.onClick ? (
            <button
              key={c.label}
              type="button"
              onClick={c.onClick}
              className="flex flex-col bg-white p-4 text-left transition-colors hover:bg-[#fafaf8] sm:p-5"
            >
              {body}
            </button>
          ) : (
            <div key={c.label} className="flex flex-col bg-white p-4 sm:p-5">
              {body}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─── Underline filter tabs (buttons) ─────────────────────────────────────
export function FilterTabs<T extends string | undefined>(props: {
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (v: T) => void;
  ariaLabel: string;
}) {
  const { options, value, onChange, ariaLabel } = props;
  return (
    <div className="-mb-px flex flex-wrap gap-x-5" role="tablist" aria-label={ariaLabel}>
      {options.map((f) => (
        <button
          key={f.label}
          type="button"
          role="tab"
          aria-selected={value === f.value}
          onClick={() => onChange(f.value)}
          className={cn(
            "min-h-[40px] border-b-2 px-0.5 text-sm transition-colors",
            value === f.value
              ? "border-ink font-medium text-ink"
              : "border-transparent text-muted hover:text-ink",
          )}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

// ─── Facts row under a record title ──────────────────────────────────────
export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-ink">{children}</dd>
    </div>
  );
}

export function FactsRow({ children }: { children: ReactNode }) {
  return <dl className="mt-3 flex flex-wrap gap-x-8 gap-y-2">{children}</dl>;
}
